import { currencyLabelOf } from '../../../../../../utils/market';
import { Upload, X, EyeOff, Eye } from 'lucide-react';
import AutoGrowTextarea from '../AutoGrowTextarea';

const INPUT_CLS = `flex w-full rounded-xl border px-3 py-1.5 placeholder:text-white/20 transition-all hover:border-white/10 focus:outline-none focus:ring-1 focus:ring-[var(--brand-500)]/30 focus:border-[var(--brand-500)]/30 h-7 bg-[#111111] text-white text-xs mt-1`;
const TEXTAREA_CLS = `flex w-full rounded-xl border border-dashed border-white/[0.06] px-3 py-2 placeholder:text-white/20 transition-all hover:border-white/15 outline-none focus:border-[var(--brand-500)]/40 min-h-[50px] bg-[#111111] text-white text-xs mt-1 resize-none`;

const COLOR_OPTIONS = [
    { value: '', label: 'Padrão' },
    { value: 'blue', label: 'Azul' },
    { value: 'green', label: 'Verde' },
    { value: 'red', label: 'Vermelho' },
];

const COLOR_ACTIVE = {
    '':      ' ring-white/80 bg-gray-600 text-white border-white/50',
    blue:    ' ring-blue-500/80 bg-blue-600 text-blue-100 border-blue-500/40',
    green:   ' ring-green-500/80 bg-green-600 text-green-100 border-green-500/40',
    red:     ' ring-red-500/80 bg-red-600 text-red-100 border-red-500/40',
};

const COLOR_INACTIVE = {
    '':      'bg-white/10 text-white/50 border-white/20',
    blue:    'bg-blue-500/10 text-blue-400/60 border-blue-500/20',
    green:   'bg-green-500/10 text-green-400/60 border-green-500/20',
    red:     'bg-red-500/10 text-red-400/60 border-red-500/20',
};

const MEDIA_ICONS = {
    image: { icon: 'fas fa-image',       color: '#22c55e' },
    video: { icon: 'fas fa-video',       color: '#ef4444' },
    audio: { icon: 'fas fa-microphone',  color: '#a855f7' },
};

function MediaThumb({ item, onRemove, onToggleSpoiler }) {
    const { icon, color } = MEDIA_ICONS[item.type] || MEDIA_ICONS.image;
    return (
        <div className="relative group bg-[#111111] border border-white/[0.06] rounded p-1">
            <div className="w-10 h-10 flex items-center justify-center">
                <i className={`${icon} text-sm`} style={{ color }} />
            </div>
            {item.fileName && (
                <p className="text-[7px] text-white/30 text-center truncate w-10 mt-0.5">
                    {item.fileName.slice(0, 9)}
                </p>
            )}
            <button
                className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={onRemove}>
                <X size={8} className="text-white" />
            </button>
            <button
                className={`absolute -bottom-1 -left-1 w-4 h-4 rounded-full flex items-center justify-center transition-all cursor-pointer ${item.spoiler ? 'bg-amber-500 opacity-100' : 'bg-white/30 opacity-0 group-hover:opacity-100'}`}
                title="Spoiler"
                onClick={onToggleSpoiler}>
                {item.spoiler
                    ? <EyeOff size={8} className="text-white" />
                    : <Eye size={8} className="text-white" />}
            </button>
        </div>
    );
}

function ColorPicker({ label, value, onChange }) {
    return (
        <div>
            <p className="text-[9px] text-white/40 mb-1">{label}</p>
            <div className="flex items-center gap-1 flex-wrap">
                {COLOR_OPTIONS.map(({ value: v, label: l }) => (
                    <button key={v} type="button"
                        className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition-all duration-150 ${value === v ? COLOR_ACTIVE[v] : COLOR_INACTIVE[v]}`}
                        onClick={() => onChange(v)}>
                        {l}
                    </button>
                ))}
            </div>
        </div>
    );
}

export default function OrderBumpNode({ id, data }) {
    const name        = data?.orderBumpName        || '';
    const price       = data?.orderBumpPrice       ?? '';
    const currencyLabel = currencyLabelOf(data?.currencyMode);

    const message     = data?.orderBumpMessage     || '';
    const media       = data?.orderBumpMedia       || [];
    const grouped     = Boolean(data?.orderBumpGrouped);
    const hideDecline      = Boolean(data?.orderBumpHideDecline);
    const disappearOnClick = Boolean(data?.orderBumpDisappearOnClick);
    const acceptColor  = data?.orderBumpAcceptColor   || '';
    const declineColor = data?.orderBumpDeclineColor  || '';
    const acceptLabel  = data?.orderBumpAcceptLabel   || '';
    const declineLabel = data?.orderBumpDeclineLabel  || '';

    const change = (patch) => data?.onChangeOrderBump?.(id, patch);
    const hasNameError  = !name.trim();
    const hasPriceError = !parseFloat(price) || parseFloat(price) <= 0;

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) data?.onOrderBumpMediaUpload?.(id, file);
        e.target.value = '';
    };

    return (
        <div className="mt-2 space-y-2 pl-2">
            {(hasNameError || hasPriceError) && (
                <div className="flex items-center gap-1.5 rounded-md bg-red-500/10 border border-red-500/30 px-2 py-1.5 text-[10px] text-red-400">
                    <i className="fas fa-exclamation-triangle" />
                    <span>{hasNameError ? 'Nome do bump obrigatório.' : 'Valor deve ser maior que zero.'}</span>
                </div>
            )}

            {/* Nome */}
            <div>
                <label className="text-[9px] text-white/40">Nome do Bump</label>
                <input type="text"
                    className={`${INPUT_CLS} ${hasNameError ? 'border-red-500/40' : 'border-white/[0.06]'}`}
                    placeholder="Ex: Bônus Premium"
                    value={name}
                    onChange={(e) => change({ orderBumpName: e.target.value })} />
            </div>

            {/* Preço */}
            <div>
                <label className="text-[9px] text-white/40">Preço {currencyLabel}</label>
                <input type="number" min="0" step="0.01"
                    className={`${INPUT_CLS} [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${hasPriceError ? 'border-red-500/40' : 'border-white/[0.06]'}`}
                    placeholder="0.00"
                    value={price}
                    onChange={(e) => change({ orderBumpPrice: e.target.value })} />
            </div>

            {/* Mídias */}
            {/* <div>
                <label className="text-[9px] text-white/40">Mídias (opcional)</label>
                {media.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1 mb-1">
                        {media.map((item) => (
                            <MediaThumb
                                key={item.id}
                                item={item}
                                onRemove={() => data?.onOrderBumpMediaRemove?.(id, item.id)}
                                onToggleSpoiler={() => data?.onOrderBumpMediaToggleSpoiler?.(id, item.id)}
                            />
                        ))}
                    </div>
                )}
                {media.length < 5 && (
                    <label className="border border-dashed rounded p-2 transition-colors cursor-pointer flex items-center justify-center gap-2 mt-1 border-white/[0.06] hover:border-white/[0.08]">
                        <input accept="image/*,video/*,audio/*" className="hidden" type="file"
                            onChange={handleFileChange}
                            disabled={Boolean(data?.uploadingOrderBumpMedia)} />
                        <Upload size={14} className="text-white/30" />
                        <span className="text-[10px] text-white/40">
                            {data?.uploadingOrderBumpMedia ? 'Enviando...' : 'Adicionar mídia'}
                        </span>
                    </label>
                )}
            </div> */}

            {/* Mensagem */}
            <div>
                <label className="text-[9px] text-white/40">Mensagem (opcional)</label>
                <AutoGrowTextarea className={TEXTAREA_CLS}
                    placeholder={`🎁 Oferta Especial!\n\n{nome_bump}\n\nClique para aceitar 👇`}
                    value={message}
                    onChange={(e) => change({ orderBumpMessage: e.target.value })} />
                <p className="text-[8px] text-white/30 mt-0.5">Variáveis: {'{nome_bump}'}</p>
            </div>

            {/* Agrupamento de mídias */}
            {media.length > 1 && (
                <div className="border-t border-white/[0.06] pt-2 mt-2">
                    <p className="text-[9px] text-white/40 mb-1.5">Envio de mídias</p>
                    <div className="flex gap-4">
                        {[{ value: false, label: 'Enviar separado' }, { value: true, label: 'Enviar agrupado' }].map(({ value, label }) => (
                            <label key={label} className="flex items-center gap-1.5 cursor-pointer">
                                <input type="radio" className="accent-[#f59e0b]"
                                    checked={grouped === value}
                                    onChange={() => change({ orderBumpGrouped: value })} />
                                <span className="text-[10px] text-white/50">{label}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {/* Botões */}
            <div className="border-t border-white/[0.06] pt-2 mt-2 space-y-2">
                <div>
                    <label className="text-[9px] text-white/40">Texto do botão Aceitar</label>
                    <input type="text"
                        className={`${INPUT_CLS} border-white/[0.06]`}
                        placeholder="✅ Aceitar"
                        value={acceptLabel}
                        onChange={(e) => change({ orderBumpAcceptLabel: e.target.value })} />
                </div>
                <ColorPicker
                    label="Cor do botão Aceitar"
                    value={acceptColor}
                    onChange={(v) => change({ orderBumpAcceptColor: v })} />
                <div>
                    <label className="text-[9px] text-white/40">Texto do botão Recusar</label>
                    <input type="text"
                        className={`${INPUT_CLS} border-white/[0.06]`}
                        placeholder="❌ Recusar"
                        value={declineLabel}
                        onChange={(e) => change({ orderBumpDeclineLabel: e.target.value })} />
                </div>
                <ColorPicker
                    label="Cor do botão Recusar"
                    value={declineColor}
                    onChange={(v) => change({ orderBumpDeclineColor: v })} />
            </div>

            {/* Toggles finais */}
            <div className="border-t border-white/[0.06] pt-2 mt-2 space-y-1.5">
                <label className="flex items-center gap-2 cursor-pointer group">
                    <input type="checkbox"
                        className="w-3.5 h-3.5 rounded border-white/[0.06] bg-[#111111] accent-[#f59e0b] cursor-pointer"
                        checked={hideDecline}
                        onChange={(e) => change({ orderBumpHideDecline: e.target.checked })} />
                    <span className="text-[10px] text-white/50 group-hover:text-white/60 transition-colors">
                        Ocultar botão de recusar
                    </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer group">
                    <input type="checkbox"
                        className="mb-2 w-3.5 h-3.5 rounded border-white/[0.06] bg-[#111111] accent-[#f59e0b] cursor-pointer"
                        checked={disappearOnClick}
                        onChange={(e) => change({ orderBumpDisappearOnClick: e.target.checked })} />
                    <span className="mb-2 text-[10px] text-white/50 group-hover:text-white/60 transition-colors">
                        Sumir ao clicar
                    </span>
                </label>
            </div>
        </div>
    );
}
